import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Header,
  Form,
  Input,
  Button,
  Message,
} from 'semantic-ui-react';
import { useMutation, gql } from '@apollo/client';

import { PATHS } from '../../paths';

const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword) {
      success
      message
    }
  }
`;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

  const passwordValid = newPassword.length >= 10;
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit = !!token && passwordValid && passwordsMatch && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const { data } = await resetPassword({ variables: { token, newPassword } });
      setResult(data?.resetPassword ?? { success: false, message: 'Something went wrong. Please try again.' });
    } catch (e: any) {
      setResult({ success: false, message: e.message ?? 'Something went wrong. Please try again.' });
    }
  };

  return (
    <Container text style={{ paddingTop: '4em', maxWidth: '480px' }}>
      <Header as="h1">Reset your password</Header>

      {!token && (
        <Message error content="This reset link is missing its token. Please use the full link you were sent." />
      )}

      {result?.success ? (
        <Message success>
          <Message.Header>Password reset</Message.Header>
          <p>{result.message}</p>
          <Link to={PATHS.LOGIN}>Go to login</Link>
        </Message>
      ) : (
        <Form onSubmit={handleSubmit} error={result?.success === false}>
          <Form.Field>
            <label>New password</label>
            <Input
              type="password"
              value={newPassword}
              placeholder="At least 10 characters"
              onChange={(e) => { setNewPassword(e.target.value); setResult(null); }}
              disabled={!token}
            />
            {newPassword.length > 0 && !passwordValid && (
              <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '4px' }}>
                Must be at least 10 characters (currently {newPassword.length})
              </div>
            )}
          </Form.Field>
          <Form.Field>
            <label>Confirm new password</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setResult(null); }}
              disabled={!token}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '4px' }}>
                Passwords do not match
              </div>
            )}
          </Form.Field>

          {result?.success === false && <Message error content={result.message} />}

          <Button primary type="submit" loading={loading} disabled={!canSubmit}>
            Reset password
          </Button>
        </Form>
      )}
    </Container>
  );
};
